import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Button, ButtonProps } from '@mantine/core';
import { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

export interface Props extends Omit<ButtonProps, 'onClick'> {
	$isChecked?: boolean;
	defaultChecked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	icon?: PredefinedIcons;
}

const BaseCheckableButton = forwardRef<HTMLButtonElement, Props>(
	({ $isChecked, defaultChecked, onCheckedChange, onClick, icon, ...props }, ref) => {
		const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
		const checked = $isChecked ?? internalChecked;

		const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
			onClick?.(event);
			const newSelected = !checked;
			if ($isChecked === undefined) {
				setInternalChecked(newSelected);
			}
			onCheckedChange?.(newSelected);
		};

		return (
			<Button
				ref={ref}
				{...props}
				component="button"
				onClick={handleClick}
				leftSection={icon ? <Icon variant={icon} size="lg" /> : undefined}
				data-checked={checked}
			/>
		);
	}
);

export const CheckableButton = styled(BaseCheckableButton)<Props>`
	background: ${({ $isChecked, theme }) =>
		$isChecked ? theme.v2.color.secondary[200] : theme.v2.color.white} !important;

	&:hover {
		background: ${({ $isChecked, theme }) =>
			$isChecked ? theme.v2.color.secondary[200] : theme.v2.color.neutral[100]} !important;
	}

	color: ${({ theme }) => theme.v2.color.black[500]};

	.mantine-Button-inner {
		gap: 0.5rem;
	}

	border: ${({ $isChecked }) => ($isChecked ? `1px solid #0D5EF2` : '1px solid transparent')};
`;
